import { Eye, Loader2, Trash2 } from "lucide-react";
import { AudioCell } from "../AudioCell";
import { StatusControl } from "../ResolveDialog";
import { SMILEYS, SENTIMENT_STYLE, fmtDate } from "../../lib/constants";

const H = ["Date", "Hotel", "Guest", "Mobile", "Category", "Rating", "Audio", "AI Transcription & Summary", "Remarks", "Status", "Action"];

export const RatingBadge = ({ r }) => {
  const s = SMILEYS[r - 1];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm font-bold ${s.bg} ${s.text}`} style={{ borderColor: `${s.color}55` }}>
      <span className="text-lg leading-none">{s.emoji}</span>{r}
    </span>
  );
};

const AiCell = ({ i }) => {
  if (i.ai_status === "pending") return <span className="inline-flex items-center gap-1.5 text-xs text-amber-700"><Loader2 className="h-3 w-3 animate-spin" /> AI processing…</span>;
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1">
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${SENTIMENT_STYLE[i.sentiment]}`}>{i.sentiment}</span>
        {i.topics.map((t) => <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{t}</span>)}
      </div>
      {i.ai_summary && <p className="text-xs font-medium text-emerald-950 line-clamp-2">{i.ai_summary}</p>}
      {i.transcript && <p className="text-[11px] italic text-slate-500 line-clamp-2">“{i.transcript}”</p>}
      {i.ai_status === "failed" && <p className="text-[11px] text-red-600">AI analysis failed — open to retry</p>}
    </div>
  );
};

export const FeedbackTable = ({ items, onResolve, onReopen, onOpen, onDelete }) => (
  <div className="glass rounded-3xl overflow-hidden rise" data-testid="feedback-table">
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1400px] text-sm">
        <thead>
          <tr className="bg-emerald-950/[0.03] text-left">
            {H.map((h) => <th key={h} className="px-4 py-3 field-label whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={H.length} className="px-4 py-12 text-center text-slate-500" data-testid="feedback-table-empty">No feedback matches these filters.</td></tr>
          )}
          {items.map((i) => (
            <tr key={i.id} data-testid={`feedback-row-${i.id}`} className={`border-t border-emerald-900/5 align-top hover:bg-white/60 transition-colors ${i.rating <= 2 && i.status !== "Resolved" ? "bg-red-50/40" : ""}`}>
              <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(i.visit_date)}</td>
              <td className="px-4 py-3 font-semibold text-emerald-950 max-w-[150px]">{i.hotel}</td>
              <td className="px-4 py-3 font-semibold whitespace-nowrap">{i.guest_name}</td>
              <td className="px-4 py-3 whitespace-nowrap text-slate-600">{i.mobile || "—"}</td>
              <td className="px-4 py-3 whitespace-nowrap">{i.category}</td>
              <td className="px-4 py-3"><RatingBadge r={i.rating} /></td>
              <td className="px-4 py-3"><AudioCell id={i.id} audio={i.audio} testId={`audio-play-${i.id}`} /></td>
              <td className="px-4 py-3 w-[320px]"><AiCell i={i} /></td>
              <td className="px-4 py-3 max-w-[200px] text-slate-600"><span className="line-clamp-3">{i.remarks || "—"}</span></td>
              <td className="px-4 py-3 max-w-[200px]">
                <StatusControl item={i} onResolve={onResolve} onReopen={onReopen} testId={`status-${i.id}`} />
                {i.resolution_remarks && <p className="mt-1 text-[11px] text-slate-500 line-clamp-2" title={i.resolution_remarks}>{i.resolution_remarks} — {i.resolved_by_name}</p>}
              </td>
              <td className="px-4 py-3">
                <button data-testid={`view-feedback-${i.id}`} onClick={() => onOpen(i)} className="inline-flex items-center gap-1 rounded-full border border-emerald-900/15 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-emerald-50 transition-colors">
                  <Eye className="h-3.5 w-3.5" /> View
                </button>
                <button data-testid={`delete-feedback-${i.id}`} onClick={() => onDelete(i)} title="Delete feedback" className="ml-1 inline-grid h-7 w-7 place-items-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
